/**
 * Storybook Mode — templates, readiness, and premium-tier hooks.
 *
 * Paywall note: full access today; gate via plan_type / template.tier later.
 */
import axios from 'axios';
import { getApiBase } from './apiBase';

const API = getApiBase();

export const storybookApi = {
  templates: () => axios.get(`${API}/storybook-api/templates`).then((r) => r.data),
  template: () => axios.get(`${API}/storybook-api/template`).then((r) => r.data),
  list: (params = {}) => {
    const q = new URLSearchParams();
    if (params.treeId) q.set('tree_id', params.treeId);
    if (params.personId) q.set('target_person_id', params.personId);
    const qs = q.toString();
    return axios.get(`${API}/storybook-api${qs ? `?${qs}` : ''}`).then((r) => r.data);
  },
  create: (payload) => axios.post(`${API}/storybook-api`, payload).then((r) => r.data),
  get: (id) => axios.get(`${API}/storybook-api/${id}`).then((r) => r.data),
  update: (id, payload) => axios.patch(`${API}/storybook-api/${id}`, payload).then((r) => r.data),
  updateJourney: (id, journeySettings) =>
    axios.patch(`${API}/storybook-api/${id}`, { journey_settings: journeySettings }).then((r) => r.data),
  saveAnswer: (id, payload) => axios.post(`${API}/storybook-api/${id}/answers`, payload).then((r) => r.data),
  ensureStory: (id, payload) =>
    axios.post(`${API}/storybook-api/${id}/answers/ensure-story`, payload).then((r) => r.data),
  createChapterDraft: (id, chapterId) =>
    axios.post(`${API}/storybook-api/${id}/chapters/draft`, { chapter_id: chapterId }).then((r) => r.data),
  people: (id) => axios.get(`${API}/storybook-api/${id}/people`).then((r) => r.data),
};

/** Client-side template catalogue (mirrors backend; used when API not loaded). */
export const STORYBOOK_TEMPLATE_OPTIONS = [
  {
    key: 'my_life_story',
    label: 'My Life Story',
    description: 'Your own autobiography — beginnings through final reflections.',
    storybookType: 'self',
    available: true,
  },
  {
    key: 'person_storybook',
    label: 'Person Storybook',
    description: 'A guided life story for someone in your family archive.',
    storybookType: 'person',
    available: true,
  },
  {
    key: 'parent_storybook',
    label: 'Parent Storybook',
    description: 'Prompts shaped for parents and carers.',
    storybookType: 'person',
    available: false,
    comingSoon: true,
  },
  {
    key: 'grandparent_legacy',
    label: 'Grandparent Legacy Book',
    description: 'Preserve a grandparent\'s memories for generations.',
    storybookType: 'person',
    available: false,
    comingSoon: true,
  },
  {
    key: 'memorial_storybook',
    label: 'Memorial Storybook',
    description: 'A gentle remembrance journey.',
    storybookType: 'person',
    available: false,
    comingSoon: true,
  },
  {
    key: 'couple_storybook',
    label: 'Couple Storybook',
    description: 'Shared prompts for two lives woven together.',
    storybookType: 'person',
    available: false,
    comingSoon: true,
  },
  {
    key: 'organisation_storybook',
    label: 'Community / Organisation Storybook',
    description: 'Stories from a group or community archive.',
    storybookType: 'person',
    available: false,
    comingSoon: true,
  },
];

export const REMINDER_FREQUENCY_OPTIONS = [
  { value: 'off', label: 'Off' },
  { value: 'weekly', label: 'Weekly gentle reminder' },
  { value: 'biweekly', label: 'Every two weeks' },
];

export function storybookHref(storybookId) {
  return `/storybook/${storybookId}`;
}

export function storybookStartHref({ treeId, personId, type = 'person', templateKey } = {}) {
  const q = new URLSearchParams();
  if (treeId) q.set('tree', treeId);
  if (personId) q.set('person', personId);
  if (type) q.set('type', type);
  if (templateKey) q.set('template', templateKey);
  const qs = q.toString();
  return `/storybook${qs ? `?${qs}` : ''}`;
}

export const STORYBOOK_STATUS_LABELS = {
  draft: 'Not started',
  in_progress: 'In progress',
  complete: 'Complete',
};

export function readinessSummary(readiness) {
  if (!readiness) return '';
  const { prompts_answered, chapters_started, chapters_total } = readiness;
  return `${prompts_answered} prompts answered · ${chapters_started} of ${chapters_total} chapters begun`;
}
